/**
 * Hospital API Hooks
 *
 * TanStack Query hooks for hospital-related operations
 */

import hospitalService from '@/api/services/hospitalService'
import {
  HospitalListParams,
  CreateHospitalPayload,
  UpdateHospitalPayload,
} from '@/types/api/hospital'
import { createApiRetryHandler } from '@/utils/apiRetry'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'

/**
 * Hook to get all hospitals (initial load)
 */
export const useAllHospitalsInit = () => {
  return useQuery({
    queryKey: ['hospitalsInit'],
    queryFn: () => hospitalService.getAllHospitalsInit(),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

/**
 * Hook to get all hospitals
 */
export const useAllHospitals = (_params: HospitalListParams) => {
  return useQuery({
    queryKey: ['hospitals'],
    queryFn: () => hospitalService.getAllHospitals(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

/**
 * Hook to get hospital cases
 */
export const useHospitalCases = () => {
  return useQuery({
    queryKey: ['hospitalCases'],
    queryFn: () => hospitalService.getHospitalCases(),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(3),
    refetchInterval: 2 * 60 * 1000,
  })
}

// MUTATION HOOKS

/**
 * Mutation hook to add hospital transfer
 */
export const useAddHospitalTransfer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['addHospitalTransfer'],
    mutationFn: ({
      hospitalId,
      caseId,
      report,
      notes,
    }: {
      hospitalId: string
      caseId: string
      report: string
      notes: string
    }) =>
      hospitalService.addHospitalTransfer(hospitalId, caseId, report, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['hospitalCases'],
      })

      toast.success('Hospital transfer added successfully')
    },
    onError: (error: any) => {
      console.error('Failed to add hospital transfer:', error)
      toast.error(error.message || 'Failed to add hospital transfer')
    },
  })
}

/**
 * Mutation hook to assign hospital case
 */
export const useAssignHospitalCase = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['assignHospitalCase'],
    mutationFn: ({
      hospitalId,
      caseId,
    }: {
      hospitalId: string
      caseId: string
    }) => hospitalService.assignHospitalCase(hospitalId, caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['hospitalCases'],
      })

      toast.success('Hospital case assigned successfully')
    },
    onError: (error: any) => {
      console.error('Failed to assign hospital case:', error)
      toast.error(error.message || 'Failed to assign hospital case')
    },
  })
}

export const useCreateHospital = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['create-hospital'],
    mutationFn: (payload: CreateHospitalPayload) =>
      hospitalService.createHospital(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] })

      toast.success('Hospital created successfully')
    },

    onError: (error: any) => {
      toast.error(error.message || 'Failed to create hospital')
    },
  })
}

/**
 * Mutation hook to update hospital details
 */
export const useUpdateHospitalDetails = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateHospitalDetails'],
    mutationFn: async ({
      hospitalId,
      updateData,
    }: {
      hospitalId: string
      updateData: UpdateHospitalPayload
    }) => {
      const response = await hospitalService.updateHospitalDetails(
        hospitalId,
        updateData
      )
      return response
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['hospitals'],
      })

      queryClient.invalidateQueries({
        queryKey: ['hospital', variables.hospitalId],
      })

      toast.success('Hospital details updated successfully')
    },
    onError: (error: any) => {
      console.error('Failed to update hospital details:', error)
      toast.error(
        error.response?.data?.message ||
          error.message ||
          'Failed to update hospital details'
      )
    },
  })
}
